import { Component } from '@theme/component';
import { isExactProductVariantIdentityResponse } from '@theme/discovery-authority-state';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.#controller.signal.aborted) this.#controller = new AbortController();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  #selectionGeneration = 0;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#selectionGeneration += 1;
    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const generation = ++this.#selectionGeneration;
    const { variantId = '', connectedProductId = '' } = event.detail ?? {};
    const expectedProductId = String(connectedProductId || event.target.getAttribute('data-product-id') || '');
    const intendedVariantId = String(variantId || '');
    this.hidden = true;

    event.promise
      .then(({ detail }) => {
        if (generation !== this.#selectionGeneration || !this.isConnected || !detail?.html) return;

        const { html, resource } = detail;
        const responseCarrier = html.querySelector('[data-product-grid-content]');
        const responseProductId = responseCarrier?.getAttribute('data-product-id')?.trim() ?? '';
        const approvedVariantId = responseCarrier?.getAttribute('data-approved-variant-id')?.trim() ?? '';
        const responsePicker = responseCarrier?.querySelector('variant-picker');
        const pickerApprovedVariantId = responsePicker?.getAttribute('data-approved-variant-id')?.trim() ?? '';
        const newMediaGallery = responseCarrier?.querySelector('media-gallery');
        const serverAllowed = isExactProductVariantIdentityResponse({
          launchAllowed:
            responseCarrier?.getAttribute('data-launch-allowed') === 'true' &&
            responsePicker?.getAttribute('data-launch-allowed') === 'true' &&
            pickerApprovedVariantId === approvedVariantId,
          expectedProductId,
          responseProductId,
          intendedVariantId,
          approvedVariantId,
          resourceId: String(resource?.id ?? ''),
          available: resource?.available === true,
        });

        if (!serverAllowed || !(newMediaGallery instanceof HTMLElement)) return;
        if (generation !== this.#selectionGeneration || !this.isConnected) return;

        newMediaGallery.hidden = false;
        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
